/**
 * Galería de los modelos de Deenex: ya pasan las reglas de Meta, sólo hay que ajustarlos.
 *
 * Es contenido de PÁGINA, no una ventana: vive en `plantillas/modelos` (el botón lleva al editor con
 * `?modelo=`) y en la plantilla de una campaña (`campanas/:id/plantilla?editor=modelos`, con `onElegir`
 * para abrir el modelo sin salir de la campaña). Antes era un cajón lateral encima de la pantalla; se
 * trabaja más cómodo con la galería a lo ancho y con el «atrás» del navegador volviendo a ella, así
 * que acá no hay overlay, ni `role="dialog"`, ni Escape: el título y el «volver» los pone la página.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import type { ModeloPlantilla } from "../types";
import { oficialApi } from "../api";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { desdeModelo, previaLocal } from "../logic/plantilla";
import { CATEGORIA } from "../logic/textos";
import { PreviaWhatsApp } from "./PreviaWhatsApp";
import { Tarjeta } from "./Tarjeta";

const R = "/activa/oficial";

export function GaleriaModelos({ onElegir }: { onElegir?: (clave: string) => void }) {
  const [modelos, setModelos] = useState<ModeloPlantilla[] | null>(null);
  const [error, setError] = useState("");
  const [intento, setIntento] = useState(0);

  useEffect(() => {
    let vivo = true;
    setError("");
    (async () => {
      try {
        const l = leerRespuesta<ModeloPlantilla[]>(await oficialApi.modelos());
        if (!vivo) return;
        if (!l.ok) setError(textoError(l));
        else if (!Array.isArray(l.data)) setError("El servidor no devolvió los modelos.");
        else setModelos(l.data);
      } catch (e) {
        if (vivo) setError(textoError(leerError(e)));
      }
    })();
    return () => { vivo = false; };
  }, [intento]);

  const claseBoton = "mt-auto inline-flex items-center justify-center gap-1.5 rounded-xl bg-violet-600 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-700";

  if (error) {
    return (
      <Tarjeta>
        <p className="text-sm font-semibold text-red-700">No pudimos traer los modelos.</p>
        <p className="text-sm text-slate-600 mt-1">{error}</p>
        <button type="button" onClick={() => setIntento((n) => n + 1)} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline">
          <RefreshCw size={14} /> Reintentar
        </button>
      </Tarjeta>
    );
  }

  // A lo ancho de la página entran tres por fila; el cajón de antes sólo tenía lugar para dos.
  if (!modelos) {
    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" aria-busy="true">
        {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-56 rounded-2xl bg-slate-200 animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {modelos.map((m) => {
        const b = desdeModelo(m);
        const tarjeta = b.tipo === "whatsapp/card";
        return (
          <div key={m.clave} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm flex flex-col gap-3">
            <div>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-slate-900">{m.nombreVisible}</p>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${m.categoria === "MARKETING" ? "bg-violet-50 text-violet-700" : "bg-slate-100 text-slate-600"}`}>
                  {CATEGORIA[m.categoria]}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1">{m.descripcion}</p>
            </div>
            <PreviaWhatsApp compacto texto={previaLocal(b)} mediaUrl={b.mediaUrl} botones={b.botones}
              pie={tarjeta ? b.pie : undefined} encabezadoTexto={tarjeta ? b.encabezadoTexto : undefined} />
            {onElegir ? (
              <button type="button" onClick={() => onElegir(m.clave)} className={claseBoton}>Usar este modelo</button>
            ) : (
              <Link to={`${R}/plantillas/nueva?modelo=${encodeURIComponent(m.clave)}`} className={claseBoton}>
                Usar este modelo
              </Link>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default GaleriaModelos;
